import { GlobalContext } from '@/components/Context/GlobalContext';
import { conditionalClasses } from '@/utils';
import { type FC, useContext, useEffect, useState } from 'react';
import { type DictionaryItem, DictionaryService } from 'service/dictionary/dictionaryService';
import { useAiDisclosure } from './AiDisclosureContext';
import { CloseIcon } from '@icons/Close';
import type { AiLabelType } from '@/types/Recipes';

export const AI_DISCLOSURE_SHIM_ID = 'ai-disclosure-shim';

const getDictionaryTitle = (dictonary: DictionaryItem[], type?: AiLabelType) => {
  if (type === 'aiEdited') {
    return DictionaryService.getCopy('aiLabelingEditedTitle', dictonary);
  }

  return DictionaryService.getCopy('aiLabelingTitle', dictonary);
};

export const AiDisclosureShim: FC = () => {
  const { isOpen, text, title, type, close } = useAiDisclosure();
  const { globalDictionary } = useContext(GlobalContext);
  const [isMounted, setIsMounted] = useState(false);

  const shownTitle = title || getDictionaryTitle(globalDictionary, type);
  const shownText = text || DictionaryService.getCopy('aiLabelingText', globalDictionary);

  useEffect(() => {
    if (isOpen) setIsMounted(true);
  }, [isOpen]);

  return (
    <div
      id={AI_DISCLOSURE_SHIM_ID}
      aria-hidden={!isOpen}
      onTransitionEnd={() => {
        if (!isOpen) setIsMounted(false);
      }}
      style={{
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
      className={conditionalClasses(
        'fixed inset-x-0 bottom-0 z-50 bg-[#EFDBB2] py-5 px-6  text-neutral-900 shadow-[0_-4px_24px_rgba(0,0,0,0.12)] transition duration-300 ease-out motion-reduce:transition-none max-w-screen-llg mx-auto rounded-t-lg',
        {
          'translate-y-0 opacity-100': isOpen,
          'translate-y-full opacity-0': !isOpen,
          'pointer-events-none': !isOpen,
          invisible: !isOpen && !isMounted,
        },
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          {shownTitle && <p className="mb-1 text-[18px] font-medium font-primary">{shownTitle}</p>}
          <p className="text-sm leading-relaxed font-neutral">{shownText}</p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close notice"
          className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-2xl leading-none text-[#A45A2A] hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900"
        >
          <span aria-hidden="true">
            <CloseIcon size={24} />
          </span>
        </button>
      </div>
    </div>
  );
};
